import React, { useState } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Menu,
  MenuItem,
  Avatar,
  Box,
  Chip,
} from '@mui/material';
import { 
  Menu as MenuIcon, 
  Logout, 
  Dashboard,
  Settings,
  Person,
  People
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { authService } from '../services/authService';
import { TextSizeControl } from './accessibility/TextSizeControl';
import { BrandLockup } from './BrandLockup';

interface HeaderProps {
  title?: string;
  onMenuClick?: () => void;
  onProfileClick?: () => void;
}

export const Header: React.FC<HeaderProps> = ({
  title = 'ShareGiver Admin',
  onMenuClick,
  onProfileClick,
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const navigate = useNavigate();
  const { state, dispatch } = useApp();

  const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setAnchorEl(null);
  };

  const handleProfileClick = () => {
    handleProfileMenuClose();
    navigate('/admin/profile');
  };

  const handleSettingsClick = () => {
    handleProfileMenuClose();
    navigate('/admin/settings');
  };

  const handleDashboardClick = () => {
    handleProfileMenuClose();
    navigate('/admin');
  };

  const handleUsersClick = () => {
    handleProfileMenuClose();
    navigate('/admin/users');
  };

  const handleLogout = () => {
    handleProfileMenuClose();
    authService.removeToken();
    dispatch({ type: 'LOGOUT' });
    navigate('/admin/auth');
  };

  const isMenuOpen = Boolean(anchorEl);

  return (
    <AppBar position="static" elevation={1} sx={{ bgcolor: 'primary.main' }}>
      <Toolbar>
        {onMenuClick && (
          <IconButton
            edge="start"
            color="inherit"
            aria-label="menu"
            onClick={onMenuClick}
            sx={{ mr: 2 }}
          >
            <MenuIcon />
          </IconButton>
        )}
        
        <BrandLockup
          height={28}
          onDark
          suffix="Admin"
          onClick={() => navigate('/admin')}
          sx={{ mr: 2 }}
        />
        {title && title !== 'ShareGiver Admin' ? (
          <Typography variant="h6" component="div" sx={{ mr: 2 }}>
            {title}
          </Typography>
        ) : null}
        <Box sx={{ flexGrow: 1 }} />
        
        {/* User profile section */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <TextSizeControl
            value={state.textSize}
            onChange={(size) => dispatch({ type: 'SET_TEXT_SIZE', payload: size })}
            invertColors
            compact
          />
          <Chip 
            label={state.user?.role?.toUpperCase() || 'ADMIN'} 
            color="primary" 
            size="small"
            sx={{ display: { xs: 'none', sm: 'flex' } }}
          />
          <Typography variant="body2" sx={{ display: { xs: 'none', md: 'block' } }}>
            {state.user ? `${state.user.firstName} ${state.user.lastName}` : 'Admin'}
          </Typography>
          <IconButton
            color="inherit"
            aria-label="profile"
            onClick={handleProfileMenuOpen}
            aria-controls={isMenuOpen ? 'profile-menu' : undefined}
            aria-haspopup="true"
            aria-expanded={isMenuOpen ? 'true' : undefined}
          >
            <Avatar sx={{ width: 32, height: 32, bgcolor: 'secondary.main' }}>
              {state.user?.firstName?.charAt(0) || state.user?.email?.charAt(0) || 'A'}
            </Avatar>
          </IconButton>
        </Box>

        {/* Profile menu */}
        <Menu
          id="profile-menu"
          anchorEl={anchorEl}
          open={isMenuOpen}
          onClose={handleProfileMenuClose}
          transformOrigin={{ horizontal: 'right', vertical: 'top' }}
          anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        >
          <MenuItem onClick={handleDashboardClick}>
            <Dashboard sx={{ mr: 1 }} />
            Dashboard
          </MenuItem>
          <MenuItem onClick={handleUsersClick}>
            <People sx={{ mr: 1 }} />
            User Management
          </MenuItem>
          <MenuItem onClick={handleProfileClick}>
            <Person sx={{ mr: 1 }} />
            Admin Profile
          </MenuItem>
          <MenuItem onClick={handleSettingsClick}>
            <Settings sx={{ mr: 1 }} />
            Settings
          </MenuItem>
          <MenuItem onClick={handleLogout}>
            <Logout sx={{ mr: 1 }} />
            Logout
          </MenuItem>
        </Menu>
      </Toolbar>
    </AppBar>
  );
};
